import parse, {
  domToReact,
  type Element,
  type HTMLReactParserOptions,
  type DOMNode,
} from 'html-react-parser';
import { ServingSizeIcon as RecipeOutsideServingSizeIcon } from '@hungryroot/components-pairing-card';
import { ThumbsUp, Coin } from '@hungryroot/icons';
import { Skeleton } from '@hungryroot/pho';
import { TransparentButton, Paragraph, Heading } from '@hungryroot/ramen';
import { InventoryStatus } from '@hungryroot/rest';
import {
  type HighlightResult,
  type PairingResult,
  type ProductResult,
} from '@hungryroot/search';
import { SearchInventoryStatus } from '../SearchInventoryStatus/SearchInventoryStatus';
import styles from './SearchResult.module.css';

type Props =
  | {
      result: PairingResult | ProductResult;
      className?: string;
      active?: boolean;
      isLoading: false;
      status: InventoryStatus;
      dietaryRestrictions?: string[];
      customerPlanServingSize?: number;
    }
  | { isLoading: true };

export function SearchResult(props: Props) {
  const { isLoading } = props;

  if (isLoading) {
    return (
      <div
        data-testid="search-result-loading"
        className={styles.container}
        key="loading"
      >
        <div className={styles.image}>
          <Skeleton className={styles['full-height']} />
        </div>
        <div className={styles.text}>
          <div className={styles.title}>
            <span>
              <Skeleton />
            </span>
          </div>
          <div>
            <Paragraph
              variant="caption"
              className={`${styles.caption} ${styles.inline}`}
            >
              <>
                <span>
                  <Skeleton />
                </span>
              </>
            </Paragraph>
          </div>
        </div>
      </div>
    );
  }

  const {
    result,
    className,
    active,
    status,
    dietaryRestrictions,
    customerPlanServingSize,
  } = props;

  const name = result._highlightResult?.name?.value || '';
  const options: HTMLReactParserOptions = {
    replace: (domNode) => {
      if ((domNode as Element)?.name === 'em') {
        return (
          <Heading variant="H6" className={styles.inline}>
            {domToReact((domNode as Element).children as DOMNode[], options)}
          </Heading>
        );
      }
    },
  };

  const cyPrefix = `search-result-${result.objectID}`;

  return (
    <TransparentButton className={styles['full-width']} ariaLabel={result.name}>
      <div
        data-testid={`search-result-${result.objectID}`}
        className={`${styles.container} ${
          active ? styles.active : ''
        } ${className}`}
        key={result.objectID}
      >
        <div className={styles.relative}>
          <div
            className={styles.image}
            style={{
              backgroundImage: `url(${
                (result as PairingResult).featured_img_url ||
                (result as ProductResult).card_image_url ||
                (result as PairingResult).index_dish_type?.thumbnail_url
              }`,
            }}
          ></div>
          {status !== InventoryStatus.Ideal && (
            <div
              className={styles['image-overlay']}
              data-testid={`search-${result.objectID}-inventory-overlay`}
            ></div>
          )}
        </div>
        <div className={styles.text}>
          <div className={styles.title} data-testid={`${cyPrefix}-title`}>
            <span>{parse(name, options)}</span>
          </div>
          {((result as PairingResult).num_ratings ?? 0) > 0 && (
            <div data-testid={`${cyPrefix}-ratings`} className={styles.flex}>
              <Paragraph variant="caption" className={styles.caption}>
                <>
                  <ThumbsUp />
                  <span>
                    {`${(
                      (result as PairingResult).recipe_score as number
                    ).toFixed(0)}`}
                    %
                    {(result as PairingResult).num_ratings &&
                      ` (${(result as PairingResult).num_ratings})`}
                  </span>
                </>
              </Paragraph>
              {(result as PairingResult).servings !== undefined && (
                <div data-testid={`${cyPrefix}-servings-outside-plan-icon`}>
                  <RecipeOutsideServingSizeIcon
                    pairingServingSize={(result as PairingResult).servings}
                    customerPlanServingSize={customerPlanServingSize}
                    additionalText={`${
                      (result as PairingResult).servings
                    } serv.`}
                  />
                </div>
              )}
            </div>
          )}
          {(result as ProductResult)?._highlightResult?.brand_name && (
            <div className={styles.caption} data-testid={`${cyPrefix}-brand`}>
              <span>
                {parse(
                  (
                    (result as ProductResult)
                      ._highlightResult as HighlightResult<ProductResult>
                  ).brand_name?.value as string,
                  options
                )}
              </span>
            </div>
          )}
          <SearchInventoryStatus
            testid={result.objectID}
            item={result}
            status={status}
            restrictions={dietaryRestrictions}
          />
        </div>
        {result.points && (
          <Heading variant="H6" className={styles.credits}>
            <Coin />
            <span
              className={styles['credits-space']}
              data-testid={`${cyPrefix}-points`}
            >
              {result.points}
            </span>
          </Heading>
        )}
      </div>
    </TransparentButton>
  );
}
